import { Fragment, useEffect, useMemo, useState } from "react";
import { Pager } from "../components/Pager";
import { Card, Chips, ErrorBanner, Pill, usePaged } from "../components/ui";
import { api, type CostPreview, type Template } from "../lib/api";

type Use = NonNullable<Template["used_by"]>[number];

/** Which shelf a message sits on: the drip it belongs to, blasts, one-offs,
 *  or nothing live. Drips by name, because each one is a hundred messages. */
function groupOf(t: Template): string {
  const use = t.used_by?.[0];
  if (!use) return "unused";
  if (use.kind === "blast") return "blasts";
  if (use.campaign_type === "one_off") return "one_offs";
  return `campaign:${use.name}`;
}

function useLabel(u: Use): string {
  if (u.kind === "blast") return `Blast · ${u.name}`;
  return u.day != null ? `${u.name} · day ${u.day}` : u.name;
}

/** The copy library.
 *
 *  Editing a message creates a NEW variant rather than changing the old one,
 *  so results recorded against the previous wording never silently
 *  re-attribute to different text.
 *
 *  Three hundred messages used to be three hundred cards, each with an open
 *  text box. Now it is a table, grouped by what uses each message and in day
 *  order within a campaign, and a message opens for editing when you click it.
 */
export function Copy() {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [writing, setWriting] = useState(false);
  const [draft, setDraft] = useState<{ name: string; key: string; body: string }>({
    name: "",
    key: "",
    body: "",
  });
  const [cost, setCost] = useState<CostPreview | null>(null);
  const [editing, setEditing] = useState<Record<number, string>>({});
  const [q, setQ] = useState("");
  const [group, setGroup] = useState<string>("all");
  const [open, setOpen] = useState<Set<number>>(new Set());

  const load = () => api.templates().then(setTemplates).catch((e) => setError(e.message));
  useEffect(() => {
    load();
  }, []);

  // Cost is shown while typing, because a single emoji can double the bill on
  // a large send and that is much cheaper to notice now than afterwards.
  useEffect(() => {
    if (!draft.body) return setCost(null);
    const t = setTimeout(() => {
      api.cost(draft.body, 1000).then(setCost).catch(() => setCost(null));
    }, 300);
    return () => clearTimeout(t);
  }, [draft.body]);

  const create = () => {
    setError(null);
    api
      .createTemplate({
        key: draft.key || draft.name.toLowerCase().replace(/\W+/g, "_").slice(0, 60),
        name: draft.name,
        variants: [{ variant_key: "A", body: draft.body, weight: 100 }],
      })
      .then(() => {
        setDraft({ name: "", key: "", body: "" });
        setWriting(false);
        load();
      })
      .catch((e) => setError(e.message));
  };

  // The shelves, biggest drips first, then blasts, one-offs, and what nothing uses.
  const groups = useMemo(() => {
    const counts = new Map<string, number>();
    for (const t of templates) counts.set(groupOf(t), (counts.get(groupOf(t)) ?? 0) + 1);
    const drips = [...counts.entries()]
      .filter(([k]) => k.startsWith("campaign:"))
      .sort((a, b) => b[1] - a[1])
      .map(([k, n]) => ({ key: k, label: k.slice("campaign:".length), count: n }));
    const tail = [
      { key: "blasts", label: "Blasts" },
      { key: "one_offs", label: "One-off campaigns" },
      { key: "unused", label: "Not in use" },
    ]
      .filter((g) => counts.has(g.key))
      .map((g) => ({ ...g, count: counts.get(g.key)! }));
    return [{ key: "all", label: "All", count: templates.length }, ...drips, ...tail];
  }, [templates]);
  const order = useMemo(() => new Map(groups.map((g, i) => [g.key, i])), [groups]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return templates
      .filter(
        (t) =>
          (group === "all" || groupOf(t) === group) &&
          (!needle ||
            t.name.toLowerCase().includes(needle) ||
            t.variants.some((v) => v.body.toLowerCase().includes(needle))),
      )
      .sort(
        (a, b) =>
          (order.get(groupOf(a)) ?? 99) - (order.get(groupOf(b)) ?? 99) ||
          (a.used_by?.[0]?.day ?? 9999) - (b.used_by?.[0]?.day ?? 9999) ||
          a.name.localeCompare(b.name),
      );
  }, [templates, q, group, order]);
  const paged = usePaged(filtered, [q, group]);

  const toggle = (id: number) =>
    setOpen((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      return n;
    });
  const stopEditing = (id: number) =>
    setEditing((e) => {
      const n = { ...e };
      delete n[id];
      return n;
    });

  return (
    <>
      <ErrorBanner error={error} />
      <div className="row">
        <h2 style={{ fontSize: 19 }}>Messages</h2>
        <div className="sp" />
        {!writing && (
          <button className="btn p" onClick={() => setWriting(true)}>
            Write a new message
          </button>
        )}
      </div>

      {writing && (
        <Card
          title="Write a new message"
          actions={
            <button className="btn s" onClick={() => setWriting(false)}>
              Close
            </button>
          }
          footer={
            <span className="dim">
              Use {"{{first_name}}"}, {"{{city}}"} or {"{{state}}"} to personalise. Missing values
              are removed cleanly rather than leaving "Hi ,".
            </span>
          }
        >
          <div className="col">
            <div className="row w">
              <input
                className="f"
                style={{ width: 280 }}
                placeholder="Name it, e.g. Waterfall Day 0"
                value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })}
              />
            </div>

            <textarea
              className="f"
              placeholder="Hi {{first_name}} — fares from {{city}} are low this week. Reply STOP to opt out."
              value={draft.body}
              onChange={(e) => setDraft({ ...draft, body: e.target.value })}
            />

            {cost && (
              <div className="row w" style={{ fontSize: 12.5 }}>
                <span className="dim">{cost.characters} characters</span>
                <Pill tone={cost.is_unicode ? "wa" : "nu"}>
                  {cost.segments} text{cost.segments > 1 ? "s" : ""} each
                </Pill>
                <span className="dim">·</span>
                <b className="mono">${cost.cost_usd.toFixed(2)} per 1,000 people</b>
              </div>
            )}
            {cost?.warning && <div className="banner warn">{cost.warning}</div>}

            {draft.body && !/stop/i.test(draft.body) && (
              <div className="banner warn">
                No opt-out line. Every marketing message needs one — add "Reply STOP to opt out."
              </div>
            )}

            <div className="row">
              <button className="btn p" disabled={!draft.name || !draft.body} onClick={create}>
                Save this message
              </button>
            </div>
          </div>
        </Card>
      )}

      <Card title="Message library">
        {templates.length === 0 ? (
          <div className="empty">No messages saved yet.</div>
        ) : (
          <>
            <div className="col" style={{ gap: 10, marginBottom: 12 }}>
              <input
                className="f"
                style={{ width: 320 }}
                placeholder="Search names and wording"
                value={q}
                onChange={(e) => setQ(e.target.value)}
              />
              <Chips value={group} onChange={setGroup} options={groups} />
            </div>

            {filtered.length === 0 ? (
              <div className="empty">No messages match.</div>
            ) : (
              <div className="tw">
                <table>
                  <thead>
                    <tr>
                      <th>Message</th>
                      <th>Used in</th>
                      <th>Wording</th>
                      <th style={{ textAlign: "right" }}>Versions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paged.shown.map((t) => {
                      const isOpen = open.has(t.id);
                      const live = t.variants.find((v) => v.status === "active") ?? t.variants[0];
                      const uses = t.used_by ?? [];
                      return (
                        <Fragment key={t.id}>
                          <tr onClick={() => toggle(t.id)} style={{ cursor: "pointer" }}>
                            <td style={{ fontWeight: 600, whiteSpace: "nowrap" }}>
                              <span className="dim" style={{ marginRight: 6 }}>
                                {isOpen ? "▾" : "▸"}
                              </span>
                              {t.name}
                            </td>
                            <td style={{ whiteSpace: "nowrap" }}>
                              {uses.length === 0 ? (
                                <span className="dim">not in use</span>
                              ) : (
                                <span className="dim" style={{ fontSize: 12 }}>
                                  {useLabel(uses[0])}
                                  {uses.length > 1 && ` +${uses.length - 1} more`}
                                </span>
                              )}
                            </td>
                            <td
                              className="dim"
                              style={{
                                fontSize: 12.5,
                                maxWidth: 520,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                              title={live?.body}
                            >
                              {live?.body}
                            </td>
                            <td className="mono" style={{ textAlign: "right" }}>
                              {t.variants.length}
                            </td>
                          </tr>

                          {isOpen && (
                            <tr>
                              <td colSpan={4} style={{ background: "var(--surf2)" }}>
                                {uses.length > 1 && (
                                  <div className="dim" style={{ fontSize: 12, marginBottom: 8 }}>
                                    Used in: {uses.map(useLabel).join(" · ")}
                                  </div>
                                )}
                                {t.variants.map((v) => {
                                  const value = editing[v.id] ?? v.body;
                                  const changed = value !== v.body;
                                  return (
                                    <div key={v.id} style={{ marginBottom: 12 }}>
                                      <div className="row w" style={{ marginBottom: 5 }}>
                                        <Pill tone="ac">{v.variant_key}</Pill>
                                        <Pill tone={v.status === "active" ? "ok" : "nu"}>
                                          {v.status}
                                        </Pill>
                                        <span className="dim mono" style={{ fontSize: 11 }}>
                                          {v.weight}% of sends
                                        </span>
                                      </div>
                                      <textarea
                                        className="f"
                                        style={{ minHeight: 70 }}
                                        value={value}
                                        onChange={(e) =>
                                          setEditing({ ...editing, [v.id]: e.target.value })
                                        }
                                      />
                                      {changed && (
                                        <div className="row" style={{ marginTop: 6 }}>
                                          <button
                                            className="btn s p"
                                            onClick={() =>
                                              api
                                                .updateVariant(t.id, v.id, value)
                                                .then(() => {
                                                  stopEditing(v.id);
                                                  load();
                                                })
                                                .catch((e) => setError(e.message))
                                            }
                                          >
                                            Save as a new version
                                          </button>
                                          <button className="btn s" onClick={() => stopEditing(v.id)}>
                                            Discard
                                          </button>
                                          <span className="dim" style={{ fontSize: 11.5 }}>
                                            The old wording is kept, so past results stay accurate.
                                          </span>
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </td>
                            </tr>
                          )}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <Pager
              page={paged.page}
              pages={paged.pages}
              total={paged.total}
              perPage={paged.perPage}
              onPage={paged.setPage}
              onPerPage={paged.setPerPage}
            />
          </>
        )}
      </Card>
    </>
  );
}
